import React, { useEffect, useMemo, useState, lazy } from 'react';
import { Box, Button, Tooltip, Typography, IconButton, Grid } from '@mui/material';
import SearchBox from '../../FlowBuilder/Common/SearchBox/index.tsx';
import { ManagementActions } from '../../../Services/_actions/Management/management.actions.ts';
import {
  PaginatedResponse,
  ProfileObject,
  FilterObj,
} from '../../../interfaces/Management.interface.ts';
import Pagination from '../../FlowBuilder/Common/Pagination/index.tsx';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue, useRecoilState } from 'recoil';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';
import ConfirmBox from '../../FlowBuilder/Common/ConfirmBox/index.tsx';
import { profileObjAtom } from '../../../Services/_states/Management/management.ts';
import HomeIcon from '@mui/icons-material/Home';
import useDebounce from '../../Shared/Delay/index.tsx';
import DynamicBreadcrumbs from '../../Shared/Breadcrumb/index.tsx';
import OrganisationDropdown from '../../Shared/OrganisationDropdown/index.tsx';
import { userInformationAtom } from '../../../Services/_states/auth.js';
import CommonTable from '../../Shared/Tables/TableV1.tsx';
// const CreateEditProfileForm = lazy(() => import('./Form.tsx'));
const CloneModal = lazy(() => import('./Clone.tsx'));
import CreateEditProfileForm from './Form.tsx';
import LoadingDisableWrapper from '../../Shared/LoadingDisableWrapper/index.tsx';
import { listFiltersAtom } from '../../Shared/SearchFilter/listFiltersAtom.js';
import { globalpermissions } from '../../../Services/Permission.js';
import { ConstantActions } from '../../../Services/_actions/constant.actions.js';

const List: React.FC = () => {
  const actions = ManagementActions();
  const navigate = useNavigate();
  const { showSnackBar } = useSnackBar();
  const loggedInUser = useRecoilValue(userInformationAtom);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [createEditModalData, setCreateEditModalData] = useState({ open: false, mode: "view" });
  const [selectedProfile, setSelectedProfile] = useState<ProfileObject | null>(null);
  const [profileObject, setProfileObject] = useRecoilState<ProfileObject>(profileObjAtom);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [profileToDelete, setProfileToDelete] = useState<ProfileObject | null>(null);
  const [indexToRemove, setIndexToRemove] = useState(null);
  const [profileResponse, setProfileResponse] = useState<PaginatedResponse>({ count: 0, list: [] });
  const [isLoading, setIsLoading] = useState(false);
  const constActions = ConstantActions();
  // const [filtersObj, setFiltersObj] = useState < FilterObj > ({
  //   limit: 20,
  //   search: '',
  //   page: 0,
  //   sortOrder: 'desc',
  //   sortField: 'created_at',
  // });

  const defaultFilters = {
    limit: 20,
    search: '',
    page: 0,
    sortOrder: 'desc',
    sortField: 'created_at',
  };

  const [allListFilters, setAllListFilters] = useRecoilState(listFiltersAtom);
  const filtersObj = allListFilters.profileList || defaultFilters;

  const setFiltersObj = (newFilters) => {
    setAllListFilters(prev => ({ ...prev, profileList: newFilters }));
  };

  const handleOpenModal = (data: ProfileObject) => {
    setSelectedProfile(data);
    setIsModalOpen(true);
  };

  const debouncedSearch = useDebounce(filtersObj.search, 500);
  useEffect(() => {
    if (debouncedSearch || debouncedSearch === '') {
      getProfileList({ ...filtersObj, search: debouncedSearch });
    }
  }, [debouncedSearch]);
  const handleSearch = (e) => {
    setFiltersObj({ ...filtersObj, search: e, page: 0 });
    // getProfileList({ ...filtersObj, search: e, page: 0 });
  }
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProfile(null);
  };
  const handleCloseCreateEditModal = (data) => {
    if (!data) {
      setCreateEditModalData({ open: false, mode: "view" });
      return;
    }
    setProfileResponse((prevResponse) => {
      if (createEditModalData?.mode === "edit") {
        return {
          ...prevResponse,
          list: prevResponse.list.map((item) =>
            item.id === profileObject.id ? { ...data } : item
          ),
        };
      } else if (createEditModalData?.mode === "create") {
        const newItem = { ...data };
        return {
          ...prevResponse,
          count: prevResponse.count + 1,
          list: [...prevResponse.list, newItem],
        };
      }
      return prevResponse;
    });
    setCreateEditModalData({ open: false, mode: "view" });
  };

  // const handleEdit = (data: ProfileObject) => {
  //   setProfileObject(data);
  //   setCreateEditModalData({ open: true, mode: "edit" });
  //   // navigate('/r/management/profile/edit/');
  // };
  const handleEdit = (data: ProfileObject, closeMenu: () => void) => {
    closeMenu();
    setTimeout(() => {
      setProfileObject(data);
      setCreateEditModalData({ open: true, mode: "edit" });
    }, 0);
  };

  const handlePermission = (data: ProfileObject) => {
    setProfileObject(data);
    navigate('/r/management/profile-permissions/edit');
  }
  const handleDelete = (profile, index) => {
    setProfileToDelete(profile);
    setIndexToRemove(index);
    setConfirmOpen(true);
  };
  const handleConfirmDelete = async (confirmed) => {
    if (confirmed && profileToDelete) {
      try {
        let res = await actions.deleteProfile(profileToDelete?.id);
        if (res?.status === 200) {
          setProfileResponse((prevState) => {
            const updatedList = prevState?.list?.filter((_, idx) => idx !== indexToRemove);
            return { ...prevState, list: updatedList };
          });
          showSnackBar('Profile deleted successfully.', 'success');
        } else {
          showSnackBar('Failed to delete profile. Please try again.', 'error');
        }
      } catch (error) {
        showSnackBar('An error occurred while deleting the profile.', 'error');
      }
    }
    setConfirmOpen(false);
    setProfileToDelete(null);
    setIndexToRemove(null);
  };

  const handleCopy = (data: ProfileObject) => {
    console.log(data);
  };

  const getProfileList = async (filterObj: FilterObj) => {
    setProfileResponse({ ...profileResponse, list: [] });
    setIsLoading(true);
    try {
      const res = await actions.getProfileList(filterObj);
      if (res?.status === 200) {
        setProfileResponse(res?.data || {});
      }
    } catch (error) {
      throw error;
    }
    setIsLoading(false);
  };

  const handleChangePage = (event, newPage) => {
    getProfileList({ ...filtersObj, page: newPage });
    setFiltersObj({ ...filtersObj, page: newPage });
  };
  const handleChangeRowsPerPage = (event) => {
    const value = parseInt(event.target.value) || 10;
    getProfileList({
      ...filtersObj,
      limit: value,
      page: 0,
    });
    setFiltersObj({
      ...filtersObj,
      limit: value,
      page: 0,
    });
  };

  const breadcrumbsData = [
    { label: <IconButton color='primary'><HomeIcon /></IconButton>, href: "/" },
    { label: 'Profile ' },
  ];

  // useEffect(() => {
  //   getProfileList(filtersObj);
  // }, []);

  const columns = useMemo<MRT_ColumnDef<ProfileObject>[]>(
    () => [
      {
        accessorKey: 'index',
        header: 'S.No.',
        enableSorting: false,
        size: 50,
        Cell: ({ row }) => {
          const rowIndex = row.index;
          const page = filtersObj?.page ?? 0; // Fallback to 0 if undefined
          const limit = filtersObj?.limit ?? 10; // Fallback to 10 if undefined
          return page * limit + rowIndex + 1;
        },
      },
      {
        accessorKey: 'name',
        header: 'Profile Name',
        enableSorting: false,
        size: 200, // Adjusted size for better spacing
      },
      {
        accessorKey: 'organisation.name',
        header: 'Organisation',
        enableSorting: false,
        size: 200,
      },
      {
        accessorKey: 'Action',
        header: 'Action',
        enableSorting: false,
        size: 30,
        Cell: ({ row }) => {
          const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
          const open = Boolean(anchorEl);

          const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
            setAnchorEl(event.currentTarget);
          };

          const handleClose = () => {
            setAnchorEl(null);
          };

          return (
            <div>
              <IconButton onClick={handleClick}>
                <MoreVertIcon />
              </IconButton>
              <Menu
                id={`menu-${row.index}`}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                  'aria-labelledby': `menu-button-${row.index}`,
                }}
              >
                {constActions.userHasPermission([
                  globalpermissions?.managementRoutes?.editProfile,
                ]) && (
                    <MenuItem
                      onClick={() => {
                        handleClose();
                        // handleEdit(row.original);
                        handleEdit(row.original, handleClose)
                      }}
                    >
                      Edit
                    </MenuItem>
                  )}
                <MenuItem
                  onClick={() => {
                    handleClose();
                    handlePermission(row?.original);
                  }}
                >
                  Permissions
                </MenuItem>

                <MenuItem
                  onClick={() => {
                    handleDelete(row.original, row.index);
                    handleClose();
                  }}
                >
                  Delete
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleOpenModal(row.original);
                    handleClose();
                  }}
                >
                  Clone Profile
                </MenuItem>
              </Menu>
            </div >
          );
        },
      },
    ],
    [filtersObj]
  );

  return (
    <Box sx={{ padding: '5px', backgroundColor: '#f8f9fa', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
      <DynamicBreadcrumbs items={breadcrumbsData} onBreadcrumbClick={(event, item: { label: string, href: string }) => {
        if (item) {
          navigate(item?.href);
        }
      }}
      />
      <Grid container sx={{ alignItems: 'center' }}>
        <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            Profile Management
          </Typography>
        </Grid>
        {constActions.userHasPermission([
          globalpermissions?.managementRoutes?.createProfile,
        ]) && (
            <Grid item xs={6} textAlign="right">
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => {
                  // navigate('/r/management/profile/create/');
                  setProfileObject({});
                  setCreateEditModalData({ open: true, mode: "create" })
                }}
              >
                Add
              </Button>
            </Grid>
          )}
      </Grid>
      {/* <Grid container sx={{ alignItems: 'center' }} className='search-box-style'>
        <Grid item xs={12} md={9} sx={{ display: 'flex', alignItems: 'center' }}>
          <SearchBox
            handleSearch={handleSearch}
            value={filtersObj?.search || ''}
          />
        </Grid>
        {loggedInUser?.is_superuser &&
          <Grid item xs={12} md={3} textAlign="right" sx={{ pb: 0.7 }}>
            <OrganisationDropdown
              value={filtersObj?.selectedOrganisation || null}
              onChange={(org) => {
                setFiltersObj({ ...filtersObj, selectedOrganisation: org || null, organisation_id: org?.organisation_id || '' });
                getProfileList({ ...filtersObj, selectedOrganisation: org || null, organisation_id: org?.organisation_id || '' })
              }}
              label={"Select Organisation"}
            />
          </Grid>
        }
      </Grid> */}
      <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ alignItems: 'center', mb: { xs: 1, sm: 2 }, mt: { xs: 1, sm: 0 } }} className='search-box-style'>
        <Grid item xs={12} sm={loggedInUser?.is_superuser ? 9 : 12}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LoadingDisableWrapper loading={isLoading}>
              <SearchBox
                handleSearch={handleSearch}
                value={filtersObj?.search || ''}
                disabled={isLoading}
              />
            </LoadingDisableWrapper>
          </Box>
        </Grid>
        {loggedInUser?.is_superuser && (
          <Grid item xs={12} sm={3}>
            <OrganisationDropdown
              value={filtersObj?.selectedOrganisation || null}
              onChange={(org) => {
                setFiltersObj({
                  ...filtersObj,
                  selectedOrganisation: org || null,
                  organisation_id: org?.organisation_id || ''
                });
                getProfileList({
                  ...filtersObj,
                  selectedOrganisation: org || null,
                  organisation_id: org?.organisation_id || ''
                });
              }}
              label={"Select Organisation"}
            />
          </Grid>
        )}
      </Grid>

      <CommonTable
        columns={columns}
        state={{
          showColumnFilters: false,
          isLoading,
        }}
        data={profileResponse?.list || []}
        muiTableContainerProps={{ sx: { maxHeight: 'calc(100vh - 320px)', width: '100%' } }}
        renderBottomToolbar={() => (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: 0, margin: 0 }}>
            <Pagination
              totalCount={profileResponse?.count || 0}
              rowsPerPage={filtersObj?.limit}
              page={filtersObj?.page}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
            />
          </Box>
        )}
      />
      <ConfirmBox
        open={confirmOpen}
        onClose={handleConfirmDelete}
        options={{
          title: 'Delete Profile',
          text: `Are you sure you want to delete the profile "${profileToDelete?.name}"?`,
          confirmButtonText: 'Delete',
          cancelButtonText: 'Cancel',
          confirmButtonColor: 'error',
          cancelButtonColor: 'default',
        }}
      />

      <CloneModal
        open={isModalOpen}
        mode={false}
        onClose={handleCloseModal}
        profile={selectedProfile || null}
      />
      {createEditModalData?.open &&
        <CreateEditProfileForm
          open={createEditModalData?.open}
          mode={createEditModalData?.mode || "view"}
          onClose={handleCloseCreateEditModal}
        />
      }
    </Box>
  );
};

export default List;
